import { useIntl } from 'react-intl';
import { firstLetterUppercase } from 'utils';

import { TimeFormat } from './types';

const UseTimeFormat = (): TimeFormat => {
  const intl = useIntl();

  const getRelativeTime = (time?: string, short?: boolean): string => {
    if (typeof time === 'undefined') return '';

    const date = Date.parse(time);
    const delta = Math.round((Date.now() - date) / 1000);

    const minute = 60;
    const hour = minute * 60;
    const day = hour * 24;

    const prefix = short ? 'SHORT_' : '';

    switch (true) {
      case delta < minute:
        return `${delta} ${intl.formatMessage({
          id: `PATIENT_LIST.TABLE.${prefix}SECONDS_AGO`,
        })}`;
      case delta < 2 * minute:
        return intl.formatMessage({
          id: `PATIENT_LIST.TABLE.${prefix}MINUTE_AGO`,
        });
      case delta < hour:
        return `${Math.floor(delta / minute)} ${intl.formatMessage({
          id: `PATIENT_LIST.TABLE.${prefix}MINUTES_AGO`,
        })}`;
      case Math.floor(delta / hour) === 1:
        return `${
          short ? `${Math.floor(delta / hour)} ` : ''
        }${intl.formatMessage({
          id: `PATIENT_LIST.TABLE.${prefix}HOUR_AGO`,
        })}`;
      case delta < day:
        return `${Math.floor(delta / hour)} ${intl.formatMessage({
          id: `PATIENT_LIST.TABLE.${prefix}HOURS_AGO`,
        })}`;
      case delta < day * 2:
        return intl.formatMessage({ id: 'PATIENT_LIST.TABLE.YESTERDAY' });
      default:
        return `${Math.floor(delta / day)} ${intl.formatMessage({
          id: `PATIENT_LIST.TABLE.${prefix}DAYS_AGO`,
        })}`;
    }
  };

  const formatToDate = (time?: string): string => {
    if (typeof time === 'undefined') return '';

    const date = new Date(time);

    const formattedDate = `${date.toLocaleDateString(intl.locale)}`;

    return formattedDate;
  };

  const formatTimeInterval = (startAt: string, endAt: string): string => {
    const startAtEvent = new Date(startAt);
    const endAtEvent = new Date(endAt);

    const weekDayLong = startAtEvent.toLocaleDateString(navigator.language, {
      weekday: 'long',
    });
    const monthLong = startAtEvent.toLocaleDateString(navigator.language, {
      month: 'long',
    });
    const startHourAndMinute = startAtEvent.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    const endHourAndMinute = endAtEvent.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${startAtEvent.getDate()}, ${firstLetterUppercase(
      weekDayLong,
    )}, ${firstLetterUppercase(
      monthLong,
    )}\n${startHourAndMinute} - ${endHourAndMinute}`;
  };

  const formatToDayAndMonth = (time?: string): string => {
    if (typeof time === 'undefined') return '';

    const date = new Date(time);

    const formattedDate = `${date.toLocaleDateString(navigator.language, {
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    })}`;

    return formattedDate;
  };

  const formatToDDMMYYYYString = (time?: string): string => {
    if (typeof time === 'undefined') return '';

    const [date] = time.split('T');

    return date.split('-').reverse().join('/');
  };

  return {
    getRelativeTime,
    formatTimeInterval,
    formatToDate,
    formatToDayAndMonth,
    formatToDDMMYYYYString,
  };
};

export { UseTimeFormat as useTimeFormat };
